export const CONFIG_STATUS = ["ACTIVE", "INACTIVE", "PAUSED", "SUSPENDED"];
export const PORTFOLIO_VISIBILITY = ["PUBLIC", "PRIVATE"];
export const MARKETS = ["SPOT", "FUTURES"];
export const ALLOCATION_TYPES = ["PERCENTAGE", "FIXED_AMOUNT"];
export const COPY_MODES = [
  "PROPORTIONAL",
  "FIXED_PERCENT_OF_ALLOCATION",
  "MULTIPLIER",
  "FIXED_AMOUNT",
];

export const COPY_MODE_LABELS = {
  PROPORTIONAL: "PROPORTIONAL (default)",
  FIXED_PERCENT_OF_ALLOCATION: "FIXED % OF ALLOCATION",
  MULTIPLIER: "MULTIPLIER",
  FIXED_AMOUNT: "FIXED AMOUNT",
};

export const COPY_MODE_HINTS = {
  PROPORTIONAL:
    "Buys/opens only: follower size = (master order / master portfolio at placement) × follower allocation. Sells and closes use the mirrored-position ledger.",
  FIXED_PERCENT_OF_ALLOCATION:
    "Buys/opens only: each trade uses a fixed % of the follower's allocation. Formerly named FIXED_RATIO. Sells/closes are position-based.",
  MULTIPLIER: "Buys/opens only: follower size = master order size × multiplier. Sells/closes are position-based.",
  FIXED_AMOUNT: "Buys/opens only: every mirrored open uses the same fixed amount. Sells/closes are position-based.",
};

/** FIXED_RATIO is a permanent API alias for FIXED_PERCENT_OF_ALLOCATION. New saves use the new name. */
export const normalizeCopyMode = (mode) => {
  if (mode === "FIXED_RATIO") return "FIXED_PERCENT_OF_ALLOCATION";
  if (COPY_MODES.includes(mode)) return mode;
  return "PROPORTIONAL";
};

export const SETTLEMENT_FREQUENCIES = ["PER_TRADE", "DAILY", "WEEKLY"];
export const ORDER_TYPES = ["MARKET", "LIMIT"];
export const LEVERAGE_MODES = ["FOLLOW_MASTER_WITH_CAP", "FIXED"];
export const MARGIN_MODES = ["ISOLATED", "CROSS", "FOLLOW_MASTER"];

/** Mongo Decimal128 sometimes arrives as { $numberDecimal: "100" } instead of a plain number. */
export const unwrapDecimal = (value) => {
  if (value == null || value === "") return value;
  if (typeof value === "object") {
    if (value.$numberDecimal != null) return unwrapDecimal(value.$numberDecimal);
    if (typeof value.toString === "function" && value._bsontype === "Decimal128") {
      return value.toString();
    }
  }
  return value;
};

export const toFiniteNumber = (value, fallback = NaN) => {
  const n = Number(unwrapDecimal(value));
  return Number.isFinite(n) ? n : fallback;
};

export const formatCopyNumber = (value, digits = 4) => {
  const raw = unwrapDecimal(value);
  if (raw == null || raw === "") return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return Number(n.toFixed(digits)).toString();
};

export const DEFAULT_MASTER_CONFIG = {
  displayName: "",
  description: "",
  profileImage: "",
  status: "ACTIVE",
  portfolioVisibility: "PUBLIC",
  allowedMarkets: ["SPOT"],
  allowedPairs: [],
  maximumFollowers: 1000,
  minimumQualifyingBalance: 500,
  allocationSettings: {
    type: "PERCENTAGE",
    percentage: 10,
    fixedAmount: null,
    minimumAllocation: 10,
    maximumAllocation: 10000,
  },
  copyTradeSettings: {
    mode: "PROPORTIONAL",
    fixedAmountPerTrade: null,
    multiplier: 1,
    minimumAmountPerTrade: 10,
    maximumAmountPerTrade: 1000,
    maximumTradesPerDay: 20,
  },
  commissionSettings: {
    masterProfitSharePercentage: 10,
    platformProfitSharePercentage: 0,
    calculateOn: "REALIZED_NET_PROFIT",
    settlementFrequency: "PER_TRADE",
  },
  spotSettings: {
    enabled: true,
    allowedOrderTypes: ["MARKET", "LIMIT"],
    maximumSlippagePercentage: 0.5,
  },
  futuresSettings: {
    enabled: false,
    maximumLeverage: 10,
    leverageMode: "FOLLOW_MASTER_WITH_CAP",
    fixedLeverage: null,
    marginMode: "ISOLATED",
    maximumMarginPerTrade: 100,
    maximumOpenPositions: 5,
  },
  riskSettings: {
    maximumDailyLossPercentage: 10,
    totalStopLossPercentage: 25,
    minimumWalletReservePercentage: 10,
  },
};

const toNumber = (value, fallback = 0) => {
  if (value === "" || value == null) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toNullableNumber = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const spotPairSymbol = (pair) => {
  const base = String(pair?.base_currency ?? pair?.baseCurrency ?? "").trim().toUpperCase();
  const quote = String(pair?.quote_currency ?? pair?.quoteCurrency ?? "").trim().toUpperCase();
  if (!base || !quote) return "";
  return `${base}${quote}`;
};

export const normalizePairs = (pairs) => {
  const seen = new Set();
  const out = [];
  (Array.isArray(pairs) ? pairs : []).forEach((raw) => {
    const symbol = String(raw || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
    if (!symbol || seen.has(symbol)) return;
    seen.add(symbol);
    out.push(symbol);
  });
  return out;
};

export const parsePairInput = (text) =>
  normalizePairs(
    String(text || "")
      .split(/[\s,]+/)
      .filter(Boolean)
  );

const mergeNested = (defaults, saved = {}) => ({
  ...defaults,
  ...saved,
});

export const mergeMasterConfig = (saved) => {
  const walk = (value) => {
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === "object") {
      if (value.$numberDecimal != null || value._bsontype === "Decimal128") {
        return unwrapDecimal(value);
      }
      const out = {};
      Object.keys(value).forEach((k) => {
        out[k] = walk(value[k]);
      });
      return out;
    }
    return value;
  };
  const src = walk(saved && typeof saved === "object" ? saved : {});
  return {
    ...DEFAULT_MASTER_CONFIG,
    ...src,
    allowedMarkets: Array.isArray(src.allowedMarkets) && src.allowedMarkets.length
      ? src.allowedMarkets
      : DEFAULT_MASTER_CONFIG.allowedMarkets,
    allowedPairs: normalizePairs(src.allowedPairs),
    allocationSettings: mergeNested(
      DEFAULT_MASTER_CONFIG.allocationSettings,
      src.allocationSettings
    ),
    copyTradeSettings: {
      ...mergeNested(
        DEFAULT_MASTER_CONFIG.copyTradeSettings,
        src.copyTradeSettings
      ),
      mode: normalizeCopyMode(
        src.copyTradeSettings?.mode || DEFAULT_MASTER_CONFIG.copyTradeSettings.mode
      ),
    },
    commissionSettings: mergeNested(
      DEFAULT_MASTER_CONFIG.commissionSettings,
      src.commissionSettings
    ),
    spotSettings: mergeNested(DEFAULT_MASTER_CONFIG.spotSettings, src.spotSettings),
    futuresSettings: mergeNested(
      DEFAULT_MASTER_CONFIG.futuresSettings,
      src.futuresSettings
    ),
    riskSettings: mergeNested(DEFAULT_MASTER_CONFIG.riskSettings, src.riskSettings),
  };
};

export const emptyMasterConfig = (master) => {
  const fallbackName = [master?.first_name, master?.last_name].filter(Boolean).join(" ").trim();
  return mergeMasterConfig({
    ...DEFAULT_MASTER_CONFIG,
    displayName: fallbackName,
  });
};

export const buildMasterConfigPayload = (form) => {
  const allocationType = form.allocationSettings?.type || "PERCENTAGE";
  const copyMode = normalizeCopyMode(form.copyTradeSettings?.mode);
  const leverageMode = form.futuresSettings?.leverageMode || "FOLLOW_MASTER_WITH_CAP";
  const markets = Array.isArray(form.allowedMarkets) ? form.allowedMarkets : [];
  const futuresEnabled = markets.includes("FUTURES") ? true : !!form.futuresSettings?.enabled;

  return {
    displayName: String(form.displayName || "").trim(),
    description: String(form.description || "").trim(),
    profileImage: String(form.profileImage || "").trim(),
    status: form.status || "ACTIVE",
    portfolioVisibility: form.portfolioVisibility || "PUBLIC",
    allowedMarkets: markets.length ? markets : ["SPOT"],
    allowedPairs: normalizePairs(form.allowedPairs),
    maximumFollowers: Math.max(1, toNumber(form.maximumFollowers, 1000)),
    minimumQualifyingBalance: Math.max(0, toNumber(form.minimumQualifyingBalance, 500)),
    allocationSettings: {
      type: allocationType,
      percentage: toNumber(form.allocationSettings?.percentage, 10),
      fixedAmount:
        allocationType === "FIXED_AMOUNT"
          ? toNullableNumber(form.allocationSettings?.fixedAmount)
          : null,
      minimumAllocation: toNumber(form.allocationSettings?.minimumAllocation, 10),
      maximumAllocation: toNumber(form.allocationSettings?.maximumAllocation, 10000),
    },
    copyTradeSettings: {
      mode: copyMode,
      fixedAmountPerTrade:
        copyMode === "FIXED_AMOUNT"
          ? toNullableNumber(form.copyTradeSettings?.fixedAmountPerTrade)
          : null,
      multiplier: toNumber(form.copyTradeSettings?.multiplier, 1),
      minimumAmountPerTrade: toNumber(form.copyTradeSettings?.minimumAmountPerTrade, 10),
      maximumAmountPerTrade: toNumber(form.copyTradeSettings?.maximumAmountPerTrade, 1000),
      maximumTradesPerDay: Math.max(1, toNumber(form.copyTradeSettings?.maximumTradesPerDay, 20)),
    },
    commissionSettings: {
      masterProfitSharePercentage: toNumber(
        form.commissionSettings?.masterProfitSharePercentage,
        10
      ),
      platformProfitSharePercentage: toNumber(
        form.commissionSettings?.platformProfitSharePercentage,
        0
      ),
      calculateOn: "REALIZED_NET_PROFIT",
      settlementFrequency: form.commissionSettings?.settlementFrequency || "PER_TRADE",
    },
    spotSettings: {
      enabled: markets.includes("SPOT") ? true : !!form.spotSettings?.enabled,
      allowedOrderTypes: Array.isArray(form.spotSettings?.allowedOrderTypes) &&
        form.spotSettings.allowedOrderTypes.length
        ? form.spotSettings.allowedOrderTypes
        : ["MARKET", "LIMIT"],
      maximumSlippagePercentage: toNumber(form.spotSettings?.maximumSlippagePercentage, 0.5),
    },
    futuresSettings: {
      enabled: futuresEnabled,
      maximumLeverage: Math.max(1, toNumber(form.futuresSettings?.maximumLeverage, 10)),
      leverageMode,
      fixedLeverage:
        leverageMode === "FIXED"
          ? toNullableNumber(form.futuresSettings?.fixedLeverage)
          : null,
      marginMode: form.futuresSettings?.marginMode || "ISOLATED",
      maximumMarginPerTrade: toNumber(form.futuresSettings?.maximumMarginPerTrade, 100),
      maximumOpenPositions: Math.max(1, toNumber(form.futuresSettings?.maximumOpenPositions, 5)),
    },
    riskSettings: {
      maximumDailyLossPercentage: toNumber(form.riskSettings?.maximumDailyLossPercentage, 10),
      totalStopLossPercentage: toNumber(form.riskSettings?.totalStopLossPercentage, 25),
      minimumWalletReservePercentage: toNumber(
        form.riskSettings?.minimumWalletReservePercentage,
        10
      ),
    },
  };
};

export const validateMasterConfig = (payload) => {
  if (!payload.displayName) {
    return "displayName is required to create a master configuration";
  }
  if (payload.minimumQualifyingBalance < 0) {
    return "minimumQualifyingBalance cannot be negative";
  }
  if (payload.allocationSettings.percentage < 0 || payload.allocationSettings.percentage > 100) {
    return "allocationSettings.percentage must be between 0 and 100";
  }
  if (payload.allocationSettings.minimumAllocation > payload.allocationSettings.maximumAllocation) {
    return "allocationSettings.minimumAllocation cannot be greater than maximumAllocation";
  }
  if (
    payload.copyTradeSettings.minimumAmountPerTrade >
    payload.copyTradeSettings.maximumAmountPerTrade
  ) {
    return "copyTradeSettings.minimumAmountPerTrade cannot be greater than maximumAmountPerTrade";
  }
  const masterShare = payload.commissionSettings.masterProfitSharePercentage;
  const platformShare = payload.commissionSettings.platformProfitSharePercentage;
  if (masterShare < 0 || masterShare > 100 || platformShare < 0 || platformShare > 100) {
    return "Commission percentages must be between 0 and 100";
  }
  if (masterShare + platformShare > 100) {
    return "masterProfitSharePercentage + platformProfitSharePercentage cannot exceed 100";
  }
  if (
    payload.futuresSettings.leverageMode === "FIXED" &&
    payload.futuresSettings.fixedLeverage != null &&
    payload.futuresSettings.fixedLeverage > payload.futuresSettings.maximumLeverage
  ) {
    return "futuresSettings.fixedLeverage cannot be greater than maximumLeverage";
  }
  if (
    !payload.futuresSettings.enabled &&
    payload.allowedMarkets.includes("FUTURES")
  ) {
    return "FUTURES cannot be in allowedMarkets while futuresSettings.enabled is false";
  }
  const pctFields = [
    payload.riskSettings.maximumDailyLossPercentage,
    payload.riskSettings.totalStopLossPercentage,
    payload.riskSettings.minimumWalletReservePercentage,
  ];
  if (pctFields.some((n) => n < 0 || n > 100)) {
    return "Risk percentages must be between 0 and 100";
  }
  return "";
};
